/**
 * Deciding how an operator-defined metric is drawn.
 *
 * Pure, because both decisions are easy to get quietly wrong: which rows of a
 * many-row table make it onto one chart, and how a value with an arbitrary
 * unit is labelled.
 */

/**
 * Lines on one chart before it stops being readable. The categorical palette
 * has six validated slots; a seventh hue would be generated rather than
 * chosen, and nineteen tangled port lines are a chart nobody can read.
 */
export const MAX_LINES = 6;

export interface RankableSeries {
  key: string;
  points: { value: number | null }[];
}

/**
 * The busiest rows first, by mean over the window.
 *
 * Mean rather than latest, so a port that happens to be idle at this instant
 * is not pushed off the chart in favour of one that blipped once. A row with
 * no readings at all ranks last rather than being dropped: it is still a row,
 * and "show all" must show it.
 */
export function rankSeries<S extends RankableSeries>(series: readonly S[]): S[] {
  const mean = (s: S) => {
    const values = s.points.map((p) => p.value).filter((v): v is number => v !== null);
    return values.length === 0 ? -Infinity : values.reduce((a, b) => a + b, 0) / values.length;
  };
  return [...series].sort(
    (a, b) => mean(b) - mean(a) || a.key.localeCompare(b.key, undefined, { numeric: true }),
  );
}

/** A value formatted with its unit, scaling the common rate units. */
export function formatValue(value: number, unit: string | null): string {
  if (unit === "bit/s" || unit === "B/s") {
    const suffix = unit === "bit/s" ? "bit/s" : "B/s";
    if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(2)} G${suffix}`;
    if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(2)} M${suffix}`;
    if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)} k${suffix}`;
    return `${value.toFixed(0)} ${suffix}`;
  }
  // Enough precision to read without inventing any: whole numbers stay whole.
  const text = Number.isInteger(value) ? String(value) : Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(2);
  if (!unit) return text;
  return unit === "%" ? `${text}%` : `${text} ${unit}`;
}
